import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConciergePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    travel_dates: '',
    num_travelers: 2,
    budget_range: '',
    special_preferences: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiry`, formData);
      toast.success('Your inquiry has been submitted! We\'ll contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        destination: '',
        travel_dates: '',
        num_travelers: 2,
        budget_range: '',
        special_preferences: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="concierge-page"
    >
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden" data-testid="concierge-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=2574&auto=format&fit=crop"
            alt="Luxury Concierge"
            className="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        </div>

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            Concierge
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary"
            data-testid="concierge-title"
          >
            Plan Your Journey
          </motion.h1>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding container-padding" data-testid="concierge-form-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <div className="text-center mb-12">
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Share your travel dreams with us. Our luxury travel advisors will craft a bespoke
                journey tailored precisely to your desires.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8" data-testid="concierge-inquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="underline-input w-full px-0 py-4 text-primary font-body"
                    data-testid="concierge-name-input"
                  />
                </div>

                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="underline-input w-full px-0 py-4 text-primary font-body"
                    data-testid="concierge-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="underline-input w-full px-0 py-4 text-primary font-body"
                    data-testid="concierge-phone-input"
                  />
                </div>

                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Destination *
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Maldives, Rajasthan, Italian Coast"
                    className="underline-input w-full px-0 py-4 text-primary font-body"
                    data-testid="concierge-destination-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Travel Dates *
                  </label>
                  <input
                    type="text"
                    name="travel_dates"
                    value={formData.travel_dates}
                    onChange={handleChange}
                    required
                    placeholder="e.g., March 2026 or Flexible"
                    className="underline-input w-full px-0 py-4 text-primary font-body"
                    data-testid="concierge-dates-input"
                  />
                </div>

                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Number of Travelers *
                  </label>
                  <input
                    type="number"
                    name="num_travelers"
                    value={formData.num_travelers}
                    onChange={handleChange}
                    required
                    min="1"
                    className="underline-input w-full px-0 py-4 text-primary font-body"
                    data-testid="concierge-travelers-input"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                  Budget Range *
                </label>
                <select
                  name="budget_range"
                  value={formData.budget_range}
                  onChange={handleChange}
                  required
                  className="underline-input w-full px-0 py-4 text-primary font-body bg-transparent"
                  data-testid="concierge-budget-select"
                >
                  <option value="" className="bg-black">Select budget range</option>
                  <option value="$10,000 - $25,000" className="bg-black">$10,000 - $25,000</option>
                  <option value="$25,000 - $50,000" className="bg-black">$25,000 - $50,000</option>
                  <option value="$50,000 - $100,000" className="bg-black">$50,000 - $100,000</option>
                  <option value="$100,000+" className="bg-black">$100,000+</option>
                </select>
              </div>

              <div>
                <label className="font-body text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                  Special Preferences
                </label>
                <textarea
                  name="special_preferences"
                  value={formData.special_preferences}
                  onChange={handleChange}
                  rows="6"
                  placeholder="Tell us about your travel style, interests, dietary requirements, special occasions, or any specific requests..."
                  className="underline-input w-full px-0 py-4 text-primary font-body resize-none"
                  data-testid="concierge-preferences-textarea"
                />
              </div>

              <div className="text-center pt-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary inline-flex items-center gap-3"
                  data-testid="concierge-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                  {!isSubmitting && <Send size={16} />}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="concierge-info-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-light text-primary mb-6">
              What Happens Next?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div>
                <div className="w-12 h-12 rounded-full border border-accent flex items-center justify-center text-accent font-body text-lg mx-auto mb-4">
                  1
                </div>
                <h3 className="font-heading text-xl text-primary mb-2">Consultation</h3>
                <p className="text-muted-foreground text-sm">
                  Our travel advisor contacts you within 24 hours for an in-depth consultation.
                </p>
              </div>
              <div>
                <div className="w-12 h-12 rounded-full border border-accent flex items-center justify-center text-accent font-body text-lg mx-auto mb-4">
                  2
                </div>
                <h3 className="font-heading text-xl text-primary mb-2">Proposal</h3>
                <p className="text-muted-foreground text-sm">
                  We craft a bespoke itinerary proposal tailored to your unique preferences.
                </p>
              </div>
              <div>
                <div className="w-12 h-12 rounded-full border border-accent flex items-center justify-center text-accent font-body text-lg mx-auto mb-4">
                  3
                </div>
                <h3 className="font-heading text-xl text-primary mb-2">Journey Begins</h3>
                <p className="text-muted-foreground text-sm">
                  Upon approval, we handle every detail and your extraordinary journey begins.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default ConciergePage;